// Personalized acceptance invitation — a private mission briefing from The Architect.
// Route: /acceptance/:token. Server validates that only accepted applicants resolve.
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { getAcceptance, acceptPlace, errText } from "@/lib/api";
import { CREATORS, ARCHITECT } from "@/data/characters";
import { ShareCard } from "@/components/acceptance/ShareCard";

const ORDER = ["black", "guide", "welcome", "reveal"];
const GUIDE_LINES = [
  "You've been observed.",
  "Your potential was noted.",
  "The Village has been watching.",
  "And now, the door is open.",
];

const laneAvatar = (lane) => (lane && CREATORS.find((c) => c.lane === lane)) || null;

const Crown = ({ animate = true, size = 56 }) => (
  <svg width={size} height={size * 0.62} viewBox="0 0 72 46" fill="none" aria-hidden="true">
    <motion.path d="M8 34 L8 12 L23 24 L36 4 L49 24 L64 12 L64 34 Z" stroke="#E7C873" strokeWidth="1.5" strokeLinejoin="round"
      initial={animate ? { pathLength: 0 } : false} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: "easeInOut" }} />
    <motion.path d="M10 40 L62 40" stroke="#C9A227" strokeWidth="1.5"
      initial={animate ? { pathLength: 0 } : false} animate={{ pathLength: 1 }} transition={{ delay: 0.7, duration: 0.35 }} />
  </svg>
);

const GoldBtn = ({ children, testId, onClick, disabled, to }) => {
  const cls = "btn-sweep inline-flex items-center justify-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_35px_rgba(201,162,39,0.35)] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60";
  return to ? <Link to={to} data-testid={testId} className={cls}>{children}</Link>
    : <button onClick={onClick} disabled={disabled} data-testid={testId} className={cls}>{children}</button>;
};

const GhostBtn = ({ children, testId, onClick, to }) => {
  const cls = "inline-flex items-center justify-center gap-2 border border-silver/40 px-8 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight";
  return to ? <Link to={to} data-testid={testId} className={cls}>{children}</Link>
    : <button onClick={onClick} data-testid={testId} className={cls}>{children}</button>;
};

const DossierRow = ({ label, value, testId }) => (
  <div className="grid gap-1 border-b border-gold/15 py-4 sm:grid-cols-[220px_1fr] sm:gap-6">
    <dt className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{label}</dt>
    <dd className="font-head text-base font-extrabold uppercase tracking-wide text-cream" data-testid={testId}>{value}</dd>
  </div>
);

const Briefing = ({ d, accepted }) => (
  <div id="briefing" className="gold-frame grain-dark relative scroll-mt-24 bg-charcoal/80 p-6 sm:p-10" data-testid="mission-briefing">
    <div className="pointer-events-none absolute inset-2 border border-gold/15" />
    <div className="relative flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className="font-cinzel text-[10px] uppercase tracking-[0.45em] text-gold">Mission Briefing</p>
        <h3 className="mt-2 font-display text-3xl uppercase tracking-tight text-metallic sm:text-4xl">Your assignment.</h3>
      </div>
      <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-silver/40">Dossier BLV / {d.member_number || "FC-XXX"}</p>
    </div>
    <dl className="relative mt-6 border-t border-gold/15">
      <DossierRow label="Clearance Status" value={accepted ? "Confirmed — place reserved" : "Accepted"} testId="briefing-clearance" />
      <DossierRow label="Team Placement" value={`Founding Cohort ${d.cohort_year}`} testId="briefing-cohort" />
      <DossierRow label="Lane Assignment" value={d.primary_lane || "Creative Cohort Member"} testId="briefing-lane" />
      <DossierRow label="Founding Member ID" value={d.member_number || "Founding Member"} testId="briefing-member-id" />
      <DossierRow label="Orientation" value={d.orientation_date || "Orientation details coming soon"} testId="briefing-orientation" />
      <DossierRow label="Next Objective" value={d.onboarding_deadline ? `Complete onboarding by ${d.onboarding_deadline}` : "Complete onboarding"} testId="briefing-next" />
    </dl>
  </div>
);

export default function Acceptance() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState("black");
  const [accepting, setAccepting] = useState(false);
  const [justAccepted, setJustAccepted] = useState(false);
  const timers = useRef([]);

  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const after = (p) => ORDER.indexOf(phase) >= ORDER.indexOf(p);
  const schedule = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  useEffect(() => {
    getAcceptance(token).then(setData).catch((e) => setError(errText(e)));
    return () => timers.current.forEach(clearTimeout);
  }, [token]);

  useEffect(() => {
    if (!data) return;
    if (reduced || data.place_accepted) {
      setPhase("reveal");
    } else {
      schedule(() => setPhase("guide"), 1000);
      schedule(() => setPhase("welcome"), 5100);
      schedule(() => setPhase("reveal"), 6400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  const goReveal = () => {
    clearTimers();
    setPhase("reveal");
  };

  const accept = async () => {
    if (accepting) return;
    clearTimers();
    setAccepting(true);
    try {
      const res = await acceptPlace(token);
      setData((prev) => ({ ...prev, ...res }));
      if (!res.already_accepted) setJustAccepted(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setAccepting(false);
    }
  };

  if (error) {
    return (
      <div className="grain-dark relative bg-onyx px-4 py-32 text-center text-cream" data-testid="acceptance-error">
        <Crown animate={false} />
        <div className="mx-auto mt-6 max-w-xl">
          <p className="font-cinzel text-[10px] uppercase tracking-[0.45em] text-gold">Access check</p>
          <h1 className="mt-4 font-display text-4xl uppercase tracking-tight text-metallic sm:text-6xl">Invitation not found</h1>
          <p className="mt-4 font-body text-silver/75" data-testid="acceptance-error-text">{error}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <GhostBtn to="/" testId="error-return-btn">Return to the Village</GhostBtn>
            <GhostBtn to="/about" testId="error-contact-btn">Contact the Program</GhostBtn>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="bg-onyx py-40 text-center font-head font-bold uppercase text-silver/50">Opening your invitation...</div>;

  const avatar = laneAvatar(data.primary_lane);
  const confirmed = data.place_accepted;
  const name = data.name || "Founding Creative";

  return (
    <div className="bg-onyx text-cream" data-testid="acceptance-page">
      {/* pre-reveal cinematic overlay — the Architect's message */}
      {createPortal(
        <AnimatePresence>
          {!after("reveal") && data && !data.place_accepted && !reduced && (
          <motion.div key="acceptance-intro" exit={{ opacity: 0, transition: { duration: 0.7 } }}
            className="grain-dark fixed inset-0 z-[80] flex items-center justify-center overflow-hidden bg-onyx px-6" data-testid="acceptance-intro">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase === "black" ? [0.1, 0.35, 0.2] : 0.16 }} transition={{ duration: 1.4 }}
              className="pointer-events-none absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ background: "radial-gradient(circle, rgba(201,162,39,0.18) 0%, transparent 60%)" }} />
            <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />

            {phase === "guide" && (
              <div className="relative max-w-xl text-center">
                <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
                  className="relative mx-auto h-28 w-28 overflow-hidden rounded-full border border-gold/40 sm:h-36 sm:w-36">
                  <img src={ARCHITECT.img} alt="" className="h-full w-full object-cover object-top" style={{ filter: "grayscale(1) brightness(0.45) contrast(1.2)" }} />
                  <div className="absolute inset-0 rounded-full shadow-[inset_0_0_45px_rgba(0,0,0,0.95),0_0_40px_rgba(201,162,39,0.2)]" />
                </motion.div>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                  className="mt-4 font-mono text-[9px] uppercase tracking-[0.35em] text-silver/50">Incoming transmission · Creative 00</motion.p>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                  className="mt-1 font-cinzel text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs">A message from The Architect</motion.p>
                <div className="mt-5 min-h-[4.5rem]">
                  {GUIDE_LINES.map((line, i) => (
                    <motion.p key={line} initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: [0, 1, 1, i === GUIDE_LINES.length - 1 ? 1 : 0], y: 0 }}
                      transition={{ delay: 0.4 + i * 0.92, duration: 0.9, times: [0, 0.25, 0.75, 1] }}
                      className="absolute inset-x-0 font-head text-xl font-bold text-cream sm:text-2xl">
                      {line}
                    </motion.p>
                  ))}
                </div>
              </div>
            )}

            {phase === "welcome" && (
              <motion.div key="welcome" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="text-center">
                <div className="flex justify-center"><Crown /></div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.4em] text-gold">Access granted</p>
                <p className="mt-3 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-6xl">Welcome to<br />Big Little Village</p>
              </motion.div>
            )}

            <button onClick={goReveal} data-testid="acceptance-skip-btn"
              className="absolute bottom-5 right-5 border border-silver/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/60 transition-colors hover:border-gold hover:text-goldlight">
              Skip to your briefing
            </button>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}

      {/* main reveal / confirmation */}
      {after("reveal") && (
        <section className="grain-dark relative overflow-hidden border-b border-gold/25 py-16 sm:py-24">
          <div className="pointer-events-none absolute left-1/2 top-1/3 h-[80vmin] w-[80vmin] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.12) 0%, transparent 60%)" }} />
          <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
          <div className="relative mx-auto max-w-[1000px] px-4 text-center sm:px-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
              <div className="flex justify-center"><Crown animate={!confirmed} /></div>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.4em] text-gold" data-testid="acceptance-clearance-line">
                {confirmed ? "Clearance confirmed" : "Access granted"}
              </p>
              <p className="mt-2 font-cinzel text-[10px] uppercase tracking-[0.4em] text-silver/60">The Architect has cleared your entry</p>
            </motion.div>

            {confirmed ? (
              <motion.div key="confirmed" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="acceptance-confirmed">
                <h1 className="mt-6 font-display text-5xl uppercase leading-[0.85] tracking-tight text-metallic sm:text-7xl">
                  Your place<br />is reserved.
                </h1>
                <p className="mt-4 font-display text-xl uppercase tracking-wide text-gold-metallic sm:text-3xl">Welcome to the Founding Cohort.</p>
                {justAccepted && <p className="mt-3 font-marker text-lg text-goldlight">mission accepted ✦</p>}
                <div className="gold-rule mx-auto mt-6 w-56" />
                <p className="mt-5 font-display text-3xl uppercase tracking-tight text-cream sm:text-5xl" data-testid="acceptance-name">{name}</p>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-goldlight" data-testid="acceptance-lane">{data.primary_lane || "Creative Cohort Member"}</p>
                <p className="mt-1 font-cinzel text-[10px] uppercase tracking-[0.35em] text-cream/80">Founding Cohort {data.cohort_year}</p>
                <p className="mt-1 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-gold" data-testid="acceptance-member-number">
                  {data.member_number ? `Founding Member \u2022 ${data.member_number}` : "Founding Member"}
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  {data.onboarding_status === "complete" ? (
                    <GoldBtn to={`/missions/${token}`} testId="mission-board-btn">Open Your Mission Board <ArrowRight size={15} /></GoldBtn>
                  ) : data.is_adult ? (
                    <GoldBtn to={`/onboarding/${token}`} testId="begin-onboarding-btn">Begin Your Briefing <ArrowRight size={15} /></GoldBtn>
                  ) : (
                    <p className="max-w-md border border-gold/40 bg-charcoal p-4 font-body text-sm text-silver" data-testid="guardian-note">
                      Because you're under 18, our team will follow up directly about guardian consent before your briefing begins. Nothing else is needed right now.
                    </p>
                  )}
                  <GhostBtn to="/" testId="return-village-btn">Return to the Village</GhostBtn>
                </div>
                <div className="mx-auto mt-14 max-w-3xl text-left"><Briefing d={data} accepted /></div>
                <div className="mt-14">
                  <p className="font-cinzel text-[10px] uppercase tracking-[0.45em] text-gold">Announce your recruitment</p>
                  <div className="mt-5"><ShareCard name={name} lane={data.primary_lane} memberNumber={data.member_number} cohortYear={data.cohort_year} /></div>
                </div>
              </motion.div>
            ) : (
              <motion.div key="reveal" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <div className="relative mt-6">
                  <h1 className="font-display uppercase leading-[0.85] tracking-tight text-metallic text-[13vw] sm:text-[7rem]" data-testid="acceptance-name">
                    {name}
                  </h1>
                  {!reduced && (
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                      <motion.div initial={{ x: "-130%" }} animate={{ x: "160%" }} transition={{ delay: 0.7, duration: 1.2, ease: "easeInOut" }}
                        className="absolute inset-y-0 w-1/3 -skew-x-12 mix-blend-screen"
                        style={{ background: "linear-gradient(90deg, transparent, rgba(231,200,115,0.2), rgba(255,255,255,0.12), transparent)", WebkitMaskImage: "linear-gradient(180deg, transparent, black 25%, black 75%, transparent)", maskImage: "linear-gradient(180deg, transparent, black 25%, black 75%, transparent)" }} />
                    </div>
                  )}
                </div>
                <p className="mt-4 font-mono text-sm uppercase tracking-[0.3em] text-goldlight" data-testid="acceptance-lane">{data.primary_lane || "Creative Cohort Member"}</p>
                <p className="mt-2 font-cinzel text-xs uppercase tracking-[0.4em] text-cream/85">Founding Cohort {data.cohort_year}</p>
                <p className="mt-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-gold" data-testid="acceptance-member-number">
                  {data.member_number ? `Founding Member \u2022 ${data.member_number}` : "Founding Member"}
                </p>

                {avatar && (
                  <div className="mx-auto mt-8 flex max-w-xs items-center gap-4 border border-gold/25 bg-charcoal/70 p-3 text-left" data-testid="lane-avatar-panel">
                    <div className="panel-mono h-16 w-16 shrink-0 overflow-hidden border border-gold/40">
                      <img src={avatar.img} alt="" className="h-full w-full object-cover object-top" />
                    </div>
                    <p className="font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-silver/70">
                      {avatar.name} holds down the {avatar.lane} lane.<br /><span className="text-goldlight">You're joining the crew.</span>
                    </p>
                  </div>
                )}

                <div className="gold-rule mx-auto mt-10 w-64" />
                <h2 className="mt-8 font-display text-3xl uppercase tracking-tight text-gold-metallic sm:text-5xl">Welcome to the team.</h2>
                <p className="mx-auto mt-4 max-w-lg font-body text-base leading-relaxed text-silver sm:text-lg">
                  Your work, perspective, and potential earned you a place in the inaugural Big Little Village cohort.
                  Your mission begins now.
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <GoldBtn onClick={accept} disabled={accepting} testId="accept-place-btn">
                    {accepting ? "Confirming clearance..." : "Accept Your Mission"}
                  </GoldBtn>
                  <GhostBtn onClick={() => document.getElementById("briefing")?.scrollIntoView({ behavior: reduced ? "auto" : "smooth" })} testId="view-briefing-btn">
                    View Your Briefing
                  </GhostBtn>
                </div>
                <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-silver/40">Accepting reserves your place — onboarding follows as your first objective</p>
                <div className="mx-auto mt-14 max-w-3xl text-left"><Briefing d={data} accepted={false} /></div>
                <p className="mt-8 font-marker text-sm text-silver/50">this isn't an internship letter. it's a recruitment. ✦</p>
              </motion.div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
